import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Minus,
  Plus,
  ShoppingCart,
  MapPin,
  Calendar,
  Sparkles,
  X,
  ArrowRight,
  Loader2,
  Trash2,
  Users,
  HandHeart,
  ExternalLink,
  Instagram,
  Link2,
  Megaphone,
} from "lucide-react";
import { PaymentModal } from "@/components/PaymentModal";
import EmailHint from "@/components/EmailHint";
import { api, fileUrl, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO_URL =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_c5288981-1cfe-4202-bbfa-1fa74e8ddd2d/artifacts/nf5at2sb_6eb7c94a-c17d-437b-a6c9-9665777e6cb6.png";

const HERO_BG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_renascer-shop/artifacts/murp6a5p_20260920_175559%281%29.webp";

const lineKey = (item_id, size, variant) => `${item_id}|${size || ""}|${variant || ""}`;

export default function LandingPage() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  // Cart lines: [{ item_id, name, price, image_path, size, variant, quantity }]
  const [cart, setCart] = useState([]);
  // Currently-selected size / variant per item
  const [selectedSize, setSelectedSize] = useState({});
  const [selectedVariant, setSelectedVariant] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", city: "" });
  const [paymentModal, setPaymentModal] = useState(null); // { links: [{label,url}] }

  useEffect(() => {
    (async () => {
      try {
        const [itemsRes, settingsRes] = await Promise.all([
          api.get("/items"),
          api.get("/settings/public"),
        ]);
        setItems(itemsRes.data);
        setSettings(settingsRes.data || {});
      } catch {
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addToCart = (item, size, variant) => {
    if ((item.sizes || []).length > 0 && !size) {
      toast.error("Selecione um tamanho antes de adicionar");
      return;
    }
    if ((item.variants || []).length > 0 && !variant) {
      toast.error(`Selecione ${item.variant_label || "uma opção"} antes de adicionar`);
      return;
    }
    setCart((c) => {
      const key = lineKey(item.id, size, variant);
      const idx = c.findIndex((l) => lineKey(l.item_id, l.size, l.variant) === key);
      if (idx >= 0) {
        const n = [...c];
        n[idx] = { ...n[idx], quantity: n[idx].quantity + 1 };
        return n;
      }
      return [
        ...c,
        {
          item_id: item.id,
          name: item.name,
          price: item.price,
          image_path: item.image_path,
          size: size || null,
          variant: variant || null,
          quantity: 1,
        },
      ];
    });
  };

  const decCart = (item_id, size, variant) =>
    setCart((c) =>
      c
        .map((l) =>
          lineKey(l.item_id, l.size, l.variant) === lineKey(item_id, size, variant)
            ? { ...l, quantity: l.quantity - 1 }
            : l
        )
        .filter((l) => l.quantity > 0)
    );

  const removeCart = (item_id, size, variant) =>
    setCart((c) => c.filter((l) => lineKey(l.item_id, l.size, l.variant) !== lineKey(item_id, size, variant)));

  const total = useMemo(
    () => cart.reduce((sum, l) => sum + l.price * l.quantity, 0),
    [cart]
  );
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);

  // Per item: qty in cart for currently-selected size/variant combo
  const cartQtyForCard = (item) => {
    const hasSizes = (item.sizes || []).length > 0;
    const hasVariants = (item.variants || []).length > 0;
    const size = hasSizes ? selectedSize[item.id] : null;
    const variant = hasVariants ? selectedVariant[item.id] : null;
    if ((hasSizes && !size) || (hasVariants && !variant)) return 0;
    const line = cart.find(
      (l) => l.item_id === item.id && (l.size || null) === (size || null) && (l.variant || null) === (variant || null)
    );
    return line ? line.quantity : 0;
  };

  // Show which sizes/variants are in cart for this item
  const linesInCartForItem = (item) => cart.filter((l) => l.item_id === item.id);

  const validPhone = (v) => v.replace(/\D/g, "").length >= 10;
  const canSubmit =
    buyer.name.trim().length >= 3 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(buyer.email) &&
    validPhone(buyer.phone) &&
    buyer.city.trim().length >= 2 &&
    cart.length > 0;

  const handleCloseOrder = async () => {
    if (!canSubmit) {
      toast.error("Preencha nome, e-mail, telefone, cidade e adicione ao menos um item");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: buyer.name,
        customer_email: buyer.email,
        customer_phone: buyer.phone,
        customer_city: buyer.city,
        items: cart.map((l) => ({
          item_id: l.item_id,
          name: l.name,
          price: l.price,
          quantity: l.quantity,
          image_path: l.image_path,
          size: l.size,
          variant: l.variant,
        })),
      });
      if (data.auto_added_entrance) {
        toast.success("Sua inscrição no evento foi incluída automaticamente!", { duration: 5000 });
      } else if (data.already_registered) {
        toast.success("Você já está inscrito no evento! Pedido registrado.", { duration: 5000 });
      } else if (data.is_subscription) {
        toast.success("Inscrição confirmada!");
      } else {
        toast.success("Pedido registrado!");
      }
      setCart([]);
      setSelectedSize({});
      setSelectedVariant({});
      setCheckoutOpen(false);
      const savedBuyerName = buyer.name;
      setBuyer({ name: "", email: "", phone: "", city: "" });

      const links = data.payment_links || [];
      const pix = data.pix || null;
      const txid = data.txid || "";
      const trackUrl = data.track_url || (txid ? `/pedido/${txid}` : "");
      const isSubscription = !!data.is_subscription;
      const alreadyRegistered = !!data.already_registered;
      // Show confirmation modal for subscriptions OR when user was already registered,
      // so the buyer always gets clear feedback (never a silent redirect).
      if (links.length === 0 && !pix) {
        toast.info("Link de pagamento ainda não configurado pelo administrador.");
      } else if (links.length === 1 && !pix && !isSubscription && !alreadyRegistered) {
        window.open(links[0].url, "_blank", "noopener,noreferrer");
      } else {
        setPaymentModal({
          links,
          pix,
          txid,
          trackUrl,
          isSubscription,
          alreadyRegistered,
          customerName: savedBuyerName,
        });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend retorna 409 com { code: "already_subscribed", txid, track_url, customer_name }
      // quando o cart só continha itens de inscrição para um cliente já inscrito.
      if (e.response?.status === 409 && detail?.code === "already_subscribed") {
        toast.error(
          `Este e-mail já possui uma inscrição${detail.customer_name ? ` — ${detail.customer_name}` : ""}. Redirecionando ao seu comprovante…`,
          { duration: 5000 }
        );
        setCart([]);
        setCheckoutOpen(false);
        if (detail.track_url) {
          setTimeout(() => { window.location.href = detail.track_url; }, 1500);
        } else if (detail.txid) {
          setTimeout(() => { window.location.href = `/pedido/${detail.txid}`; }, 1500);
        }
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Erro ao enviar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-cream)" }}>
      {/* ANNOUNCEMENT BANNER */}
      {settings.announcement_active && (settings.announcement_title || settings.announcement_body) && (
        <div className="relative z-30 bg-gradient-to-r from-amber-100 via-orange-100 to-amber-100 border-b border-amber-200" data-testid="announcement-banner">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-red-700 to-orange-600 text-white flex items-center justify-center shrink-0">
              <Megaphone className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              {settings.announcement_title && (
                <div className="font-semibold text-stone-900 text-sm" data-testid="announcement-title">
                  {settings.announcement_title}
                </div>
              )}
              {settings.announcement_body && (
                <div className="text-sm text-stone-700 whitespace-pre-wrap" data-testid="announcement-body">
                  {settings.announcement_body}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#fdfbf7]/85 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Renascer" className="h-11 w-11 object-contain" data-testid="nav-logo" />
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-stone-900">Renascer</div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500">Encontrão Nacional</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-instagram-link"
                aria-label="Instagram"
                className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-600 hover:text-orange-700 hover:bg-orange-50 transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {settings.linktree_url && (
              <a
                href={settings.linktree_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-linktree-link"
                aria-label="Linktree"
                className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-600 hover:text-orange-700 hover:bg-orange-50 transition-colors"
              >
                <Link2 className="h-4 w-4" />
              </a>
            )}
            <button
              data-testid="nav-cart-btn"
              onClick={() => setCheckoutOpen(true)}
              className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 bg-white hover:border-orange-500 hover:text-orange-700 transition-colors text-sm font-medium"
            >
              <ShoppingCart className="h-4 w-4" />
              <span className="hidden sm:inline">Carrinho</span>
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full flame-gradient text-white text-[11px] font-bold flex items-center justify-center"
                  data-testid="nav-cart-count"
                >
                  {cartCount}
                </span>
              )}
            </button>
            <Link
              to="/admin/login"
              data-testid="nav-admin-link"
              className="text-xs sm:text-sm text-stone-500 hover:text-stone-900 transition-colors px-3 py-2"
            >
              Admin
            </Link>
            {settings.subscription_active && (
              <Link
                to="/inscricao"
                data-testid="nav-subscribe-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-orange-700 hover:text-orange-900 border border-orange-300 hover:border-orange-500 rounded-full px-3 py-2 transition-colors"
              >
                Inscrever-se
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,25,23,0.55) 0%, rgba(127,29,29,0.45) 55%, rgba(28,25,23,0.85) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-200/40 bg-amber-100/10 backdrop-blur text-amber-100 text-xs uppercase tracking-widest mb-6" data-testid="hero-badge">
                <Sparkles className="h-3 w-3" /> Até lá!
              </div>
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[0.95] tracking-tight"
                data-testid="hero-title"
              >
                {settings.event_title || "Aguardem o 4º Encontrão Nacional Renascer — Até lá!"}
              </h1>
              <p
                className="mt-6 text-lg sm:text-xl text-stone-100/90 max-w-xl leading-relaxed"
                data-testid="hero-subtitle"
              >
                {settings.event_subtitle || "Um encontro para renovar a fé, celebrar juntos e reacender a chama do coração."}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="#vendas"
                  data-testid="hero-cta-shop"
                  className="btn-primary-flame inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold"
                >
                  Garantir meus itens <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#evento"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors"
                >
                  Sobre o evento
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-8 rounded-full blur-3xl opacity-40 flame-gradient" />
                <img
                  src={LOGO_URL}
                  alt="Logo Renascer"
                  className="relative h-64 w-64 sm:h-80 sm:w-80 object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-16"
          style={{ background: "linear-gradient(180deg, transparent, var(--bg-cream))" }}
        />
      </section>

      {/* EVENT INFO — bento */}
      <section id="evento" className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 lg:row-span-2 rounded-3xl bg-stone-900 text-white p-10 relative overflow-hidden">
            <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full flame-gradient opacity-30 blur-3xl" />
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300/80 mb-4">Sobre o encontro</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold leading-tight">
              Um chamado para <span className="brand-text">renascer</span> em comunhão.
            </h2>
            <p className="mt-6 text-stone-300 leading-relaxed">
              {settings.event_description ||
                "Um dia inteiro de louvor, palavra e comunhão fraterna. Um espaço para reacender a chama, fortalecer a fé e celebrar em família a graça de Deus na nossa jornada."}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <img src={LOGO_URL} alt="" className="h-14 w-14 object-contain" />
              <div className="text-sm text-stone-300">
                <div className="font-semibold text-white">Renascer</div>
                <div>Movimento da Igreja Católica Apostólica Romana</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 rounded-3xl p-8 lg:p-10 bg-white border border-stone-200 card-warm-shadow flex items-start gap-6">
            <div className="h-14 w-14 rounded-2xl flame-gradient flex items-center justify-center shrink-0">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-stone-500">Data</div>
              <div className="font-display text-2xl lg:text-3xl font-semibold mt-1" data-testid="event-date">
                {settings.event_date || "A ser anunciada em breve"}
              </div>
              <p className="text-stone-600 mt-3">Reserve na agenda. Traga sua família e amigos.</p>
            </div>
          </div>

          <div className="lg:col-span-4 rounded-3xl p-8 bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-200/60 card-warm-shadow">
            <MapPin className="h-6 w-6 text-orange-700" />
            <div className="text-xs uppercase tracking-widest text-stone-500 mt-4">Local</div>
            <div className="font-display text-xl lg:text-2xl font-semibold mt-1 text-stone-900" data-testid="event-location">
              {settings.event_location || "Local a definir"}
            </div>
            {settings.event_map_url && (
              <a
                href={settings.event_map_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-map-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700 hover:text-orange-900 transition-colors"
              >
                Ver no mapa <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          <div className="lg:col-span-3 rounded-3xl p-8 bg-stone-900 text-white flex flex-col justify-between">
            <Users className="h-6 w-6 text-amber-300" />
            <div>
              <div className="font-display text-4xl font-bold">+2 mil</div>
              <div className="text-sm text-stone-400 mt-1">irmãos esperados</div>
            </div>
          </div>
        </div>
      </section>

      {/* SALES SECTION */}
      <section id="vendas" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Loja do evento</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 text-stone-900">
              Escolha seus itens
            </h2>
            <p className="text-stone-600 mt-3 max-w-xl">
              Camisetas, cadernos e recordações para viver e lembrar este momento.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-stone-300 p-16 text-center bg-white" data-testid="no-items-empty">
            <HandHeart className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Em breve novos itens</div>
            <p className="text-stone-500 mt-2 text-sm">
              A equipe está preparando com carinho os itens do evento.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it) => {
              const sizes = it.sizes || [];
              const variants = it.variants || [];
              const variantLabel = it.variant_label || "Opção";
              const hasSizes = sizes.length > 0;
              const hasVariants = variants.length > 0;
              const size = hasSizes ? selectedSize[it.id] || null : null;
              const variant = hasVariants ? selectedVariant[it.id] || null : null;
              const qty = cartQtyForCard(it);
              const lines = linesInCartForItem(it);
              const canAdd = (!hasSizes || size) && (!hasVariants || variant);
              return (
                <article
                  key={it.id}
                  data-testid={`item-card-${it.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-stone-200 card-warm-shadow hover:-translate-y-1 hover:shadow-xl transition-transform duration-300 flex flex-col"
                >
                  <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                    {it.image_path ? (
                      <img
                        src={fileUrl(it.image_path)}
                        alt={it.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <ShoppingCart className="h-12 w-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display text-xl font-semibold text-stone-900" data-testid={`item-name-${it.id}`}>
                      {it.name}
                    </h3>
                    {it.description && <p className="text-sm text-stone-600 mt-1">{it.description}</p>}

                    {hasSizes && (
                      <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Tamanho</div>
                        <div className="flex flex-wrap gap-1.5" data-testid={`item-sizes-${it.id}`}>
                          {sizes.map((s) => {
                            const active = size === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                data-testid={`item-size-${it.id}-${s}`}
                                onClick={() => setSelectedSize((m) => ({ ...m, [it.id]: s }))}
                                className={`min-w-[42px] px-3 h-9 rounded-full text-sm font-semibold border transition-all ${
                                  active
                                    ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-md"
                                    : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                                }`}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {hasVariants && (
                      <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">{variantLabel}</div>
                        <div className="flex flex-wrap gap-1.5" data-testid={`item-variants-${it.id}`}>
                          {variants.map((v) => {
                            const active = variant === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                data-testid={`item-variant-${it.id}-${v.replace(/\s+/g, "-")}`}
                                onClick={() => setSelectedVariant((m) => ({ ...m, [it.id]: v }))}
                                className={`px-3 h-9 rounded-full text-sm font-semibold border transition-all ${
                                  active
                                    ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-md"
                                    : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                                }`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {lines.length > 0 && (
                      <div className="mt-3 text-[11px] text-stone-500">
                        No carrinho:{" "}
                        {lines.map((l, i) => (
                          <span key={lineKey(l.item_id, l.size, l.variant)} className="font-semibold text-stone-700">
                            {l.quantity}×{l.size ? ` ${l.size}` : ""}
                            {l.variant ? ` · ${l.variant}` : ""}
                            {i < lines.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-5 pt-2">
                      <div className="brand-text font-display text-2xl font-bold" data-testid={`item-price-${it.id}`}>
                        {formatBRL(it.price)}
                      </div>
                      {qty === 0 ? (
                        <button
                          data-testid={`item-add-btn-${it.id}`}
                          onClick={() => addToCart(it, size, variant)}
                          disabled={!canAdd}
                          className="btn-primary-flame px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                        >
                          <Plus className="h-4 w-4" /> Adicionar
                        </button>
                      ) : (
                        <div className="inline-flex items-center gap-1 border border-stone-300 rounded-full p-1 bg-white">
                          <button
                            data-testid={`item-dec-btn-${it.id}`}
                            onClick={() => decCart(it.id, size, variant)}
                            className="h-8 w-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-700"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="min-w-[24px] text-center font-semibold" data-testid={`item-qty-${it.id}`}>
                            {qty}
                          </span>
                          <button
                            data-testid={`item-inc-btn-${it.id}`}
                            onClick={() => addToCart(it, size, variant)}
                            className="h-8 w-8 rounded-full flame-gradient text-white flex items-center justify-center"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Sticky total bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-stone-200 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Total geral</div>
              <div className="font-display text-2xl font-bold text-stone-900" data-testid="sticky-total">
                {formatBRL(total)}
              </div>
            </div>
            <button
              data-testid="sticky-close-order-btn"
              onClick={() => setCheckoutOpen(true)}
              className="btn-primary-flame px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2"
            >
              Fechar pedido <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Checkout drawer */}
      {checkoutOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={(e) => e.target === e.currentTarget && setCheckoutOpen(false)}
        >
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" />
          <div className="relative w-full sm:max-w-md h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">Seu pedido</div>
                <div className="font-display text-2xl font-bold">Fechar pedido</div>
              </div>
              <button
                data-testid="close-checkout-btn"
                onClick={() => setCheckoutOpen(false)}
                className="h-9 w-9 rounded-full hover:bg-stone-100 flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cart.length === 0 ? (
                <div className="text-center py-10">
                  <ShoppingCart className="h-10 w-10 text-stone-300 mx-auto" />
                  <p className="mt-4 text-stone-500 text-sm">Seu carrinho está vazio.</p>
                </div>
              ) : (
                <>
                  <div
                    className="flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50"
                    data-testid="checkout-inscricao-notice"
                  >
                    <div className="h-9 w-9 shrink-0 rounded-full flame-gradient text-white flex items-center justify-center">
                      <HandHeart className="h-4.5 w-4.5" />
                    </div>
                    <div className="text-sm text-stone-700 leading-relaxed">
                      <div className="font-semibold text-stone-900">Inscrição no evento</div>
                      <div className="text-xs text-stone-600 mt-0.5">
                        Se este é seu primeiro pedido, incluiremos a <b>Entrada</b> automaticamente para confirmar sua inscrição. Se você já é inscrito, seguimos direto com os itens escolhidos.
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {cart.map((l) => (
                      <div
                        key={lineKey(l.item_id, l.size, l.variant)}
                        data-testid={`cart-item-${l.item_id}-${l.size || "none"}-${(l.variant || "none").replace(/\s+/g, "-")}`}
                        className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl border border-stone-200"
                      >
                        <div className="h-14 w-14 rounded-lg overflow-hidden bg-stone-200 shrink-0">
                          {l.image_path ? (
                            <img src={fileUrl(l.image_path)} alt={l.name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-stone-400">
                              <ShoppingCart className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{l.name}</div>
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {l.size && (
                              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200">
                                {l.size}
                              </span>
                            )}
                            {l.variant && (
                              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                {l.variant}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-stone-500 mt-1">
                            {l.quantity} × {formatBRL(l.price)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-sm">{formatBRL(l.price * l.quantity)}</div>
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <button
                              onClick={() => decCart(l.item_id, l.size, l.variant)}
                              className="h-6 w-6 rounded-full bg-white border border-stone-300 hover:border-orange-500 flex items-center justify-center"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => removeCart(l.item_id, l.size, l.variant)}
                              className="h-6 w-6 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 flex items-center justify-center"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-stone-200">
                    <div className="text-sm text-stone-500 mb-3">Seus dados</div>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="buyer-name">Nome completo</Label>
                        <Input
                          id="buyer-name"
                          data-testid="buyer-name-input"
                          value={buyer.name}
                          onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                          placeholder="Ex: Maria da Silva"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyer-email">Email</Label>
                        <Input
                          id="buyer-email"
                          data-testid="buyer-email-input"
                          type="email"
                          value={buyer.email}
                          onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                          placeholder="maria@email.com"
                          className="mt-1"
                        />
                        <EmailHint
                          value={buyer.email}
                          onAccept={(fixed) => setBuyer({ ...buyer, email: fixed })}
                          testid="buyer-email-hint"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyer-phone">Telefone</Label>
                        <Input
                          id="buyer-phone"
                          data-testid="buyer-phone-input"
                          value={buyer.phone}
                          onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                          placeholder="(11) 99999-9999"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyer-city">
                          Cidade <span className="text-red-600">*</span>
                        </Label>
                        <Input
                          id="buyer-city"
                          data-testid="buyer-city-input"
                          value={buyer.city}
                          onChange={(e) => setBuyer({ ...buyer, city: e.target.value })}
                          placeholder="Ex: Cidade Ocidental — GO"
                          className="mt-1"
                        />
                        <p className="text-[11px] text-stone-500 mt-1">
                          Obrigatório para a sua inscrição no evento.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-stone-200 bg-white">
              <div className="flex justify-between items-baseline mb-4">
                <span className="text-stone-600">Total geral</span>
                <span className="font-display text-3xl font-bold brand-text" data-testid="checkout-total">
                  {formatBRL(total)}
                </span>
              </div>
              <button
                data-testid="submit-order-btn"
                onClick={handleCloseOrder}
                disabled={!canSubmit || submitting}
                className="btn-primary-flame w-full py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {submitting ? "Enviando..." : "Confirmar e pagar"}
              </button>
              <p className="text-[11px] text-stone-500 text-center mt-3">
                Ao confirmar, você será redirecionado ao link de pagamento configurado pelo evento.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment links modal (multiple links case) */}
      <PaymentModal payment={paymentModal} onClose={() => setPaymentModal(null)} kind="order" />

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-9 w-9 object-contain" />
            <div className="text-sm text-stone-600">© 2026 Renascer</div>
          </div>
          <div className="flex items-center gap-3">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                aria-label="Instagram"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-orange-700 border border-stone-200 hover:border-orange-500 rounded-full px-3 py-1.5 transition-colors"
              >
                <Instagram className="h-3.5 w-3.5" /> Instagram
              </a>
            )}
            {settings.linktree_url && (
              <a
                href={settings.linktree_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linktree-link"
                aria-label="Linktree"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 hover:text-orange-700 border border-stone-200 hover:border-orange-500 rounded-full px-3 py-1.5 transition-colors"
              >
                <Link2 className="h-3.5 w-3.5" /> Linktree
              </a>
            )}
            <div className="text-xs text-stone-500 ml-2">Feito com fé e esperança</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
